import React from "react";
import { connect } from "react-redux";
import { FlatButton, Card, CardText } from "material-ui";
import moment from "moment";
import _ from "lodash";
import PropTypes from "prop-types";
import { Helmet } from "react-helmet";
import { commitMutation, graphql } from "react-relay";
import environment from "../../relay/environment";

import newStyles from "./styles.scss";
import TravelerListItem from "./TravelerListItem";
import OurismanTravelTheme from "../../ourismanTravelTheme";
import EditTrip from "./EditTrip";
import VoidTrip from "./VoidTrip";
import SegmentListItem from "./SegmentListItem";
import AddSegment from "./AddSegment";
import AddTraveler from "./AddTraveler";
import NoteListItem from "./NoteListItem";
import AddNote from "./AddNote";
import Notes from "../NotesComponent";
import TaskListItem from "./TaskListItem";

const defaultState = {
  addingTraveler: false,
  addingSegment: false,
  editing: false,
  cancelling: false,
  cancelReason: "",
  showNotes: false,
  addingNote: false,
  titleHeight: 109,
  showVIPNotes: false,
  disableVIPHide: false,
  showAllTasks: false
};

@connect(state => ({
  windowHeight: state.Responsive.get("windowHeight")
}))
export default class TripDetails extends React.Component {
  static propTypes = {
    trip: PropTypes.shape().isRequired,
    windowHeight: PropTypes.number.isRequired,
    users: PropTypes.arrayOf(PropTypes.object).isRequired
  };

  constructor(props) {
    super(props);

    this.state = defaultState;
  }

  setTitleHeight = () => {
    const { titleHeight: oldTitleHeight } = this.state;
    const titleHeight = document.getElementById("title").clientHeight;
    if (titleHeight !== oldTitleHeight) {
      this.setState({ titleHeight });
    }
  };

  componentDidMount() {
    this.setTitleHeight();
  }

  componentDidUpdate() {
    this.setTitleHeight();
  }

  componentWillReceiveProps(nextProps) {
    const {
      trip: { id: oldId }
    } = this.props;
    const {
      trip: { id: newId }
    } = nextProps;

    const oldTitleHeight = this.state.titleHeight;
    const newTitleHeight = document.getElementById("title").clientHeight;

    const newState = defaultState;
    newState.titleHeight = newTitleHeight;

    if (oldId !== newId || oldTitleHeight !== newTitleHeight) {
      this.setState(newState);
    }
  }

  toggle = prop => () => {
    const key = `${prop}`;
    const oldValue = this.state[key];
    const newState = {};
    newState[key] = !oldValue;
    this.setState(newState);
  };

  noteState = disableVIPHide => {
    this.setState({ disableVIPHide });
  };

  addTask = taskType => () => {
    const {
      trip: { id: tripId }
    } = this.props;

    commitMutation(environment, {
      mutation: graphql`
        mutation TripDetails_AddTask_Mutation($input: AddTaskInput!) {
          addTask(input: $input) {
            trip {
              id
              welcomeHome
              tasks {
                taskType
                description
                completed
                voided
                history {
                  name
                  action
                  timestamp
                }
              }
            }
          }
        }
      `,
      variables: { input: { tripId, taskType } },
      onError: err => alert(err)
    });
  };

  render() {
    const { trip, users, windowHeight } = this.props;
    const {
      primaryTravelerName,
      destination,
      dateString,
      endDate,
      inbox,
      owner,
      travelers,
      category: { name: category },
      cancelled,
      segments,
      notes: tripNotes,
      created,
      modified,
      fired,
      lowPriority,
      highPriority,
      insideCircle,
      needsTraining,
      needsVetting,
      prospect,
      id,
      vipNotes,
      tasks
    } = trip;
    const numTravelers = travelers.length;
    const numAdults = travelers.filter(traveler => traveler.adult).length;
    const numChildren = numTravelers - numAdults;
    const numSegments = segments.length;

    const totalTasks = tasks.length;
    const toDo = tasks.filter(task => !task.completed && !task.voided);
    const completed = tasks.filter(task => task.completed && !task.voided);
    const voided = tasks.filter(task => task.voided);

    const wHTasks = toDo.filter(
      task => task.taskType === "DRAFT_WH" || task.taskType === "CANCEL_WH"
    );
    const hasWHTask = wHTasks.length > 0;
    const needsWHTask = !hasWHTask && moment(endDate) > moment();

    let addWHTaskType;
    if (needsWHTask) {
      const numDrafted = completed.filter(task => task.taskType === "DRAFT_WH")
        .length;
      const numCancelled = completed.filter(
        task => task.taskType === "CANCEL_WH"
      ).length;

      if (numDrafted > numCancelled) {
        addWHTaskType = "cancel_wh";
      } else {
        addWHTaskType = "draft_wh";
      }
    } else {
      addWHTaskType = null;
    }

    const numToDo = toDo.length;
    const numCompleted = completed.length;
    const numVoided = voided.length;

    const combinedNotes = _.concat(
      tripNotes,
      ...segments.map(segment =>
        segment.notes.map(note => ({
          ...note,
          segmentName: segment.name
        }))
      )
    ).sort(({ created: firstDate }, { created: secondDate }) => {
      if (firstDate < secondDate) {
        return 1;
      } else if (firstDate > secondDate) {
        return -1;
      }
      return 0;
    });
    const numNotes = combinedNotes.length;

    const {
      editing,
      cancelling,
      addingTraveler,
      addingSegment,
      showNotes,
      addingNote,
      titleHeight,
      showVIPNotes,
      disableVIPHide,
      showAllTasks
    } = this.state;

    const preventAction =
      editing || cancelling || addingTraveler || addingSegment;
    const preventCancel =
      preventAction ||
      _.some(
        segments.map(segment => {
          if (segment.booked && !segment.cancelled) {
            return true;
          } else if (
            segment.cancelled &&
            (segment.invoiced || segment.vipSent)
          ) {
            return true;
          }

          return false;
        })
      );

    const detailsContainerStyle = {
      maxHeight: windowHeight - titleHeight - 64,
      top: titleHeight
    };

    const createdMoment = moment(created);
    const modifiedMoment = moment(modified);
    const createdStamp = moment(createdMoment).format(
      "dddd, MMMM Do YYYY, h:mm a"
    );
    const createdFromNow = moment(createdMoment).fromNow();
    const modifiedStamp = moment(modifiedMoment).format(
      "dddd, MMMM Do YYYY, h:mm a"
    );
    const modifiedFromNow = moment(modifiedMoment).fromNow();

    const flags = [
      fired ? "fired" : false,
      prospect ? "prospect" : false,
      insideCircle ? "insideCircle" : false,
      lowPriority ? "lowPriority" : false,
      needsTraining ? "needsTraining" : false,
      needsVetting ? "needsVetting" : false,
      highPriority ? "highPriority" : false
    ].filter(flag => flag !== false);
    const flagLabels = {
      fired: "fired",
      prospect: "prospect",
      insideCircle: "Inside Circle",
      lowPriority: "low priority",
      needsTraining: "needs training",
      needsVetting: "needs vetting",
      highPriority: "BMOC"
    };

    const pageTitle = `${primaryTravelerName} ${dateString} ${destination}`;

    return (
      <div style={styles.tripContainer}>
        <Helmet>
          <title>{pageTitle}</title>
        </Helmet>
        <div className={newStyles.tripTitle} id="title">
          <div className={newStyles.leftBlock}>
            <span
              className={
                cancelled ? newStyles.cancelledPrimary : newStyles.titlePrimary
              }
            >
              {primaryTravelerName}
            </span>
            <span
              className={
                cancelled
                  ? newStyles.cancelledSecondary
                  : newStyles.titleSecondary
              }
            >
              {destination}
            </span>
            <span
              className={
                cancelled
                  ? newStyles.cancelledSecondary
                  : newStyles.titleSecondary
              }
            >
              {dateString}
            </span>
          </div>
          <div className={newStyles.middleBlock}>
            <div className={newStyles.flags}>
              {/* <div>Tier {tier}</div> */}
              {flags.map((flag, index) => (
                <div key={index} className={newStyles[flag]}>
                  {flagLabels[flag]}
                </div>
              ))}
            </div>
          </div>
          <div className={newStyles.rightBlock}>
            <span>Contact Inbox:</span>
            <span>{inbox || "none set"}</span>
            <span>Assigned To:</span>
            <span>{`${owner.firstName} ${owner.lastName[0]}.`}</span>
            <span>Trip Type:</span>
            <span>{category}</span>
            <span className={newStyles.button}>
              <FlatButton
                label="Edit Details"
                primary
                onTouchTap={this.toggle("editing")}
                disabled={preventAction}
              />
            </span>
            <span className={newStyles.button}>
              {cancelled ? (
                <span
                  style={{
                    color: "red",
                    textTransform: "uppercase"
                  }}
                >
                  Voided
                </span>
              ) : (
                <FlatButton
                  label="Void Trip"
                  secondary
                  onTouchTap={this.toggle("cancelling")}
                  disabled={preventCancel}
                />
              )}
            </span>
          </div>
          <div className={newStyles.timestamp}>
            <div>
              <span className={newStyles.label}>Created:</span>
              <span className={newStyles.data}>{createdStamp}</span>
              <span className={newStyles.data}>({createdFromNow})</span>
            </div>
            <div>
              <span className={newStyles.label}>Modified:</span>
              <span className={newStyles.data}>{modifiedStamp}</span>
              <span className={newStyles.data}>({modifiedFromNow})</span>
            </div>
          </div>
        </div>
        <div style={detailsContainerStyle} className={newStyles.tripDetails}>
          {editing ? (
            <EditTrip
              trip={trip}
              users={users}
              handleSave={this.toggle("editing")}
            />
          ) : null}
          {cancelling ? (
            <VoidTrip trip={trip} handleSave={this.toggle("cancelling")} />
          ) : null}

          <h4>
            Tasks ({numToDo} to do, {numCompleted} completed, {numVoided}{" "}
            voided)
            <FlatButton
              label={showAllTasks ? "Hide" : "Show All"}
              onTouchTap={this.toggle("showAllTasks")}
              disabled={numToDo === totalTasks}
            />
            <FlatButton
              label="Add Welcome Home Task"
              disabled={!needsWHTask}
              onTouchTap={this.addTask(addWHTaskType)}
            />
          </h4>
          <ol>
            {toDo.map((task, index) => (
              <TaskListItem key={index} index={index} task={task} />
            ))}
            {showAllTasks
              ? completed.map((task, index) => (
                  <TaskListItem key={index} index={index} task={task} />
                ))
              : null}
            {showAllTasks
              ? voided.map((task, index) => (
                  <TaskListItem key={index} index={index} task={task} />
                ))
              : null}
          </ol>

          <h4>
            Notes ({numNotes} notes){" "}
            <FlatButton
              label={showNotes ? "Hide" : "Show"}
              onTouchTap={this.toggle("showNotes")}
            />
          </h4>
          {showNotes ? (
            <ol>
              {combinedNotes.map((note, index) => (
                <NoteListItem key={index} index={index} note={note} />
              ))}
            </ol>
          ) : null}
          <FlatButton
            label="Add Note"
            disabled={addingNote}
            onTouchTap={this.toggle("addingNote")}
          />
          {addingNote ? (
            <AddNote trip={trip} onComplete={this.toggle("addingNote")} />
          ) : null}

          <h4>
            Travelers ({numAdults} adults, {numChildren} children)
          </h4>
          <ol>
            {travelers.map((traveler, index) => (
              <TravelerListItem
                key={index}
                index={index}
                trip={trip}
                traveler={traveler}
              />
            ))}
          </ol>
          <FlatButton
            label="Add Traveler"
            disabled={preventAction}
            onTouchTap={this.toggle("addingTraveler")}
          />
          {addingTraveler ? (
            <AddTraveler
              index={numTravelers}
              trip={trip}
              onComplete={this.toggle("addingTraveler")}
            />
          ) : null}

          <h4>
            VIP Notes{" "}
            <FlatButton
              label={showVIPNotes ? "Hide" : "Show"}
              onTouchTap={this.toggle("showVIPNotes")}
              disabled={disableVIPHide}
            />
          </h4>
          {showVIPNotes ? (
            <Card style={styles.vipNotes}>
              <CardText>
                <Notes
                  id={id}
                  fieldName="vipNotes"
                  value={vipNotes}
                  onChange={this.noteState}
                />
              </CardText>
            </Card>
          ) : null}

          <h4>Itinerary ({numSegments} segments)</h4>
          <ol>
            {segments.map((segment, index) => (
              <SegmentListItem
                key={index}
                index={index}
                trip={trip}
                segment={segment}
              />
            ))}
          </ol>
          <FlatButton
            label="Add Segment"
            disabled={preventAction}
            onTouchTap={this.toggle("addingSegment")}
          />
          {addingSegment ? (
            <AddSegment trip={trip} onComplete={this.toggle("addingSegment")} />
          ) : null}
        </div>
      </div>
    );
  }
}

const styles = {
  titleContainer: {
    position: "fixed",
    background: OurismanTravelTheme.palette.primary3Color,
    zIndex: 10
  },
  titleTop: {
    display: "flex",
    justifyContent: "space-between",
    flexWrap: "wrap"
  },
  titleBlock: {
    display: "flex",
    flexDirection: "column",
    flex: "2 1 content",
    marginRight: 30
  },
  titlePrimary: {
    fontSize: 36
  },
  titleSecondary: {
    fontSize: 24
  },
  actionBlock: {
    display: "flex",
    flexDirection: "column",
    flex: "1 0 content"
  },
  actionItem: {
    display: "flex",
    alignItems: "baseline",
    justifyContent: "space-between"
  },
  actionItemContent: {
    flex: "1 0 content"
  },
  vipNotes: {
    margin: 5
  }
};



// WEBPACK FOOTER //
// /src/components/Trips/TripDetails.js